import { Illustration, Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { PageContent } from "@src/appV2/redesign/WorkerShifts/components/PageContent";

import { LoadingButton } from "../../components/LoadingButton";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";
import { OnboardingPageHeader, type OnboardingPageHeaderProps } from "../SharedPageHeader";
import { OnboardingTitle } from "../SharedTitle";
import { ONBOARDING_STAGES } from "../util/constants";
import { type OnboardingPersonalIdVerificationAnalytics } from "./types";

interface OnboardingPersonalIdInReviewProps extends Pick<OnboardingPageHeaderProps, "progress"> {
  isLoading: boolean;
  onBack: () => void;
  onNext: () => void;
  analytics: OnboardingPersonalIdVerificationAnalytics;
}

export function OnboardingPersonalIdInReview({
  isLoading,
  onBack,
  onNext,
  analytics,
  progress,
}: OnboardingPersonalIdInReviewProps) {
  useLogEffect(APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_IN_REVIEW_PAGE_VIEWED, {
    ...analytics,
  });

  return (
    <PageWrapper variant="tertiary">
      <OnboardingPageHeader hideBackButton progress={progress} onClickBack={onBack} />
      <PageContent>
        <Stack gap={4}>
          <OnboardingTitle text="ID Under Review" />
          <Illustration type="document-review" sx={{ alignSelf: "center" }} />
          <Text variant="body1">
            We need more time to review your ID submission before we can verify your identity. You
            may continue signing up for Clipboard while it&apos;s under review.
          </Text>
          <Text variant="body1">
            If we need additional information from you, we&apos;ll reach out via email.
          </Text>
        </Stack>
      </PageContent>
      <PageFooterWrapper>
        <LoadingButton
          fullWidth
          size="large"
          isLoading={isLoading}
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
              stepCompleted: ONBOARDING_STAGES.PERSONAL_ID_VERIFICATION,
              personalIdState: "in-review",
            });
            onNext();
          }}
        >
          Continue
        </LoadingButton>
      </PageFooterWrapper>
    </PageWrapper>
  );
}
